import {Box, Button, Grid, Paper, Typography} from "@mui/material"
import React, {useState} from "react"
import {DATE_FORMAT} from "src/utils/constants"
import DatePickerSelect from "src/components/date/date-picker-select"
import AlertMessage from "src/components/alert-message"
import SelectValue from "src/components/select-value"
import dayjs from "dayjs"
import {IntegrationService} from "src/services/integration-service"
import {DataGrid} from "@mui/x-data-grid"
import SkeletonTable from "src/components/skeleton-table"

// Lista de rotinas local para esta tela apenas
const routineDataWorkflowList = [
    {value: "cliente_precos_log_workflow", description: "Cliente Preços Log"},
    {value: "curvas_workflow", description: "Curvas"},
    {value: "liquidez_workflow", description: "Liquidez"},
    {value: "precos_processados_workflow_v2", description: "Preços Processados v2"},
    {value: "precos_eventos_financeiros_workflow", description: "Preços Eventos Financeiros"},
    {value: "precos_eventos_lista_workflow", description: "Preços Eventos Lista"},
    {value: "precos_tunel_workflow", description: "Preços Túneis"},
    {value: "spreads_brutos_tratados_workflow", description: "Spreads Brutos Tratados"},
    {value: "spreads_negociados_workflow", description: "Spreads Negociados"},
    {value: "spreads_emissao_workflow", description: "Spreads Emissão"},
    {value: "spread_relativo_workflow", description: "Spread Relativo"},
    {value: "tratamento_risk_free_workflow", description: "Tratamento Risk Free"},
    {value: "curvas_volatilidade_workflow", description: "Curvas Volatilidade"}
]

const ReportDataRoutinesWorkflowPage = () => {
    const [loading, setLoading] = useState(false)
    const [dateSelected, setDateSelected] = useState(dayjs().format(DATE_FORMAT))
    const [alertMessageState, setAlertMessageState] = useState({open: false, message: "", type: ""})
    const [routineSelected, setRoutineSelected] = useState(null)
    const [columns, setColumns] = useState([])
    const [tableData, setTableData] = useState([])

    const integrationService = new IntegrationService()

    const alterarValor = (value) => setRoutineSelected(value)
    const onChangeDatePickerDate = (value) => setDateSelected(value.format(DATE_FORMAT))
    const closeAlert = () => setAlertMessageState({...alertMessageState, open: false})
    const openAlert = (message, type) => setAlertMessageState({open: true, message, type})

    const name = routineSelected
    const dateSelectedDate = dateSelected

    const formatHeader = (key) => {
        return key
            .replace(/_/g, " ")
            .replace(/^\w/, (c) => c.toUpperCase())
    }

    const handleResponse = (response) => {
        try {
            const jsonData = JSON.parse(response.text)
            console.log("Dados recebidos da API:", jsonData)

            if (Array.isArray(jsonData)) {
                if (jsonData.length === 0) return openAlert("Nenhum dado disponível.", "warning")

                const allFields = new Set()
                jsonData.forEach((item) => {
                    if (item.report) {
                        Object.keys(item.report).forEach((key) => {
                            if (key !== "metadata_output") allFields.add(key)
                        })
                    }
                })

                const reportFields = Array.from(allFields)

                const groupedByLote = {}
                jsonData.forEach((item) => {
                    const lote = item.lote_id
                    if (!groupedByLote[lote]) groupedByLote[lote] = []
                    groupedByLote[lote].push(item)
                })

                const table = []
                Object.entries(groupedByLote).forEach(([loteId, items]) => {
                    items.forEach((item, idx) => {
                        const row = {
                            id: `${loteId}_${idx}`,
                            lote_id: idx === 0 ? loteId : "",
                            data_referencia: item.data_referencia,
                        }

                        reportFields.forEach((field) => {
                            let value = item.report?.[field]
                            if (typeof value === "boolean") value = value ? "Sim" : "Não"
                            row[field] = value ?? ""
                        })

                        table.push(row)
                    })
                })

                const generatedColumns = [
                    {field: "lote_id", headerName: "Lote ID", width: 120},
                    {field: "data_referencia", headerName: "Data Referência", width: 180},
                    ...reportFields.map((field) => ({
                        field,
                        headerName: formatHeader(field),
                        width: 180,
                    })),
                ]

                setColumns(generatedColumns)
                setTableData(table)
            } else {
                const table = Object.keys(jsonData).map((key, index) => ({
                    id: `${key}_${index}_${Date.now()}`,
                    dados: key,
                    qtde: jsonData[key],
                }))

                const generatedColumns = [
                    {field: "dados", headerName: "Dados", width: 200},
                    {field: "qtde", headerName: "Qtde", width: 200},
                ]

                setColumns(generatedColumns)
                setTableData(table)
            }
        } catch (err) {
            console.error("Erro ao processar resposta:", err)
            openAlert("Erro ao processar resposta.", "error")
        } finally {
            setLoading(false)
        }
    }

    const handleError = (error) => {
        console.error("Erro ao buscar dados:", error)
        openAlert("Erro ao buscar dados.", "error")
        setLoading(false)
    }

    const inicialRotina = () => {
        if (!name || !dateSelectedDate) return openAlert("Selecione uma rotina e data", "warning")

        setLoading(true)
        setColumns([])
        setTableData([])

        integrationService
            .getReportDataInfo(name, dateSelectedDate)
            .then(handleResponse)
            .catch(handleError)
    }

    const inicialRotinaPeriodo = () => {
        if (!name || !dateSelectedDate) return openAlert("Selecione uma rotina e data", "warning")

        setLoading(true)
        setColumns([])
        setTableData([])

        integrationService
            .getReportDataPeriodoInfo(name, dateSelectedDate)
            .then(handleResponse)
            .catch(handleError)
    }

    return (
        <div>
            <AlertMessage {...alertMessageState} close={closeAlert}/>

            <Box sx={{width: "auto"}}>
                <Grid container alignItems="flex-start" justifyContent="flex-start" spacing={3}>
                    <Grid item sm={12} md={6} xl={4} xs={12}>
                        <Typography variant="h5">Relatório de Dados</Typography>
                    </Grid>
                </Grid>
            </Box>

            <br/>

            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3} xl={4}>
                    <SelectValue
                        label="Selecione uma rotina"
                        value={routineSelected}
                        id="select-routine"
                        changeValue={alterarValor}
                        options={routineDataWorkflowList}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} md={9} xl={4}>
                    <DatePickerSelect
                        label="Data de Exibição"
                        onChange={onChangeDatePickerDate}
                        value={dateSelected}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} md={3} xl={4}>
                    <Button variant="contained" fullWidth onClick={inicialRotina}>
                        Consultar
                    </Button>
                </Grid>

                <Grid item xs={12} md={3} xl={4}>
                    <Button variant="contained" fullWidth onClick={inicialRotinaPeriodo}>
                        Consultar 15 dias
                    </Button>
                </Grid>
            </Grid>

            <Grid container direction="row" justifyContent="space-between" spacing={2} sx={{mt: 4, mb: 2}}>
                <Grid item>
                    <Typography variant="h5">Relatório</Typography>
                </Grid>

                <Grid item xs={12}>
                    {loading ? (
                        <SkeletonTable/>
                    ) : (
                        <Paper style={{height: 600, width: "100%"}}>
                            <DataGrid
                                autoHeight={true}
                                rows={tableData}
                                columns={columns}
                                getRowId={(row) => row.id}
                                pageSize={5}
                                rowsPerPageOptions={[5]}
                            />
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </div>
    )
}

export default ReportDataRoutinesWorkflowPage
